import { useQuery } from '@tanstack/react-query'
import Gate from '../../model/Gate'
import GateSubmission from '../../model/GateSubmission'
import { dataSource } from '../../service/domain/_service.config'
import { useChallengeId } from '../challenge/useChallengeId'
import { useChallengeAccess } from '../membership/useChallengeAccess'

export const GATES_QUERY_KEY = (challengeID: string) => ['gates', challengeID] as const

export const GATE_SUBMISSIONS_QUERY_KEY = (teamID: string, gateID: string) =>
  ['gateSubmissions', teamID, gateID] as const

export const TEAM_GATE_SUBMISSIONS_QUERY_KEY = (teamID: string) =>
  ['teamGateSubmissions', teamID] as const

export function useGates() {
  const challengeID = useChallengeId()

  return useQuery<Gate[]>({
    queryKey: GATES_QUERY_KEY(challengeID),
    queryFn: () => dataSource.getGates(challengeID),
    enabled: !!challengeID,
  })
}

export function useGateSubmissions(gateID: string) {
  const { teamID } = useChallengeAccess()

  return useQuery<GateSubmission[]>({
    queryKey: teamID
      ? GATE_SUBMISSIONS_QUERY_KEY(teamID, gateID)
      : ['gateSubmissions', 'disabled'],
    queryFn: () => {
      if (!teamID) throw new Error('User has no team')
      return dataSource.getGateSubmissions(gateID, teamID)
    },
    enabled: !!teamID && !!gateID,
  })
}

export function useTeamGateSubmissions() {
  const { teamID } = useChallengeAccess()

  return useQuery<GateSubmission[]>({
    queryKey: teamID ? TEAM_GATE_SUBMISSIONS_QUERY_KEY(teamID) : ['teamGateSubmissions', 'disabled'],
    queryFn: () => {
      if (!teamID) throw new Error('User has no team')
      return dataSource.getTeamGateSubmissions(teamID)
    },
    enabled: !!teamID,
  })
}

export function useGate(gateID: string) {
  const { data: gates, ...rest } = useGates()
  return {
    data: gates?.find((gate) => gate.id === gateID),
    ...rest,
  }
}
